<template>
  <app-modal :modalIn="show" @close="close" modalClass="modal-md">
    <template #header>
      {{ editMode ? 'Edit' : 'Add' }} {{ transactionTypeLabel }}
    </template>
    <div>
      <!-- Quick action for zero shipping -->
      <div v-if="transactionType === 'SHIPPING_LABEL' && !editMode" class="mb-3">
        <button
          type="button"
          class="btn btn-outline-secondary w-100"
          @click="setNoShippingCosts"
          :disabled="submitting"
        >
          <i class="bi bi-box-seam me-2"></i>
          No Shipping Costs
        </button>
        <hr class="my-3">
        <div class="text-center text-muted small">Or enter custom amount</div>
      </div>

      <div class="row mb-3">
        <div class="col-7">
          <label class="form-label">Amount</label>
          <div class="input-group">
            <span class="input-group-text">$</span>
            <money-input
              v-model="form.amount"
              class="form-control"
              placeholder="0.00"
            />
          </div>
        </div>
        <div class="col-5">
          <label class="form-label">Type</label>
          <div class="btn-group w-100" role="group">
            <input
              type="radio"
              class="btn-check"
              id="bookingDebit"
              value="DEBIT"
              v-model="form.bookingEntry"
            >
            <label class="btn btn-outline-danger btn-sm text-nowrap" for="bookingDebit">
              <i class="bi bi-dash-circle me-1"></i>Cost
            </label>
            <input
              type="radio"
              class="btn-check"
              id="bookingCredit"
              value="CREDIT"
              v-model="form.bookingEntry"
            >
          <label class="btn btn-outline-success btn-sm text-nowrap" for="bookingCredit">
              <i class="bi bi-plus-circle me-1"></i>Refund
            </label>
          </div>
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">Date</label>
        <input
          type="date"
          v-model="form.date"
          class="form-control"
        />
      </div>

      <div class="mb-3">
        <label class="form-label">Memo <span class="text-muted">(optional)</span></label>
        <input
          type="text"
          v-model="form.memo"
          class="form-control"
          placeholder="e.g., Non-platform expense"
        />
      </div>

      <hr>

      <div class="d-flex gap-3 justify-content-between">
        <button class="btn btn-outline-secondary" @click="close" :disabled="submitting">
          Cancel
        </button>
        <button class="btn btn-primary" @click="submit" :disabled="!canSubmit || submitting">
          <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
          {{ submitting ? 'Saving...' : (editMode ? 'Update' : 'Save') }}
        </button>
      </div>
    </div>
  </app-modal>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import AppModal from '@/app/components/Utils/AppModal.vue'
import MoneyInput from '@/app/components/Utils/MoneyInput.vue'

const store = useStore()

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  orderId: {
    type: [String, Number],
    required: true
  },
  transactionType: {
    type: String,
    default: 'SHIPPING_LABEL'
  },
  existingTransaction: {
    type: Object,
    default: null
  },
  initialBookingEntry: {
    type: String,
    default: 'DEBIT'
  }
})

const emit = defineEmits(['saved', 'close'])

const submitting = ref(false)
const form = ref({
  amount: null,
  date: new Date().toISOString().split('T')[0],
  bookingEntry: 'DEBIT',
  memo: ''
})

// Computed
const editMode = computed(() => !!props.existingTransaction)

const transactionTypeLabel = computed(() => {
  const labels = {
    'SHIPPING_LABEL': 'Shipping',
    'COGS': 'Cost of Goods',
    'FEE': 'Fee',
    'MARKETING': 'Marketing',
    'REFUND': 'Refund'
  }
  return labels[props.transactionType] || props.transactionType
})

const canSubmit = computed(() => {
  // Amount can be 0 (for no shipping costs), but must be defined
  return form.value.amount !== null && form.value.date
})

// Watch for existing transaction to populate form
watch(() => props.existingTransaction, (transaction) => {
  if (transaction) {
    form.value = {
      amount: Math.abs(parseFloat(transaction.transaction_amount) || 0),
      date: transaction.transaction_date
        ? new Date(transaction.transaction_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      bookingEntry: parseFloat(transaction.transaction_amount) < 0 ? 'DEBIT' : 'CREDIT',
      memo: transaction.transaction_memo || ''
    }
  }
}, { immediate: true })

// Watch for show to reset form when opening fresh
watch(() => props.show, (isOpen) => {
  if (isOpen && !props.existingTransaction) {
    resetForm()
  }
})

function resetForm() {
  form.value = {
    amount: null,
    date: new Date().toISOString().split('T')[0],
    bookingEntry: props.initialBookingEntry,
    memo: ''
  }
}

function setNoShippingCosts() {
  form.value.amount = 0
  form.value.memo = 'No shipping costs'
  submit()
}

function close() {
  emit('close')
}

async function submit() {
  if (!canSubmit.value || submitting.value) return

  submitting.value = true

  // Build payload - amount stored as positive, booking_entry determines sign
  const payload = {
    order_id: props.orderId,
    transaction_type: props.transactionType,
    transaction_amount: Math.abs(form.value.amount),
    transaction_date: form.value.date,
    transaction_memo: form.value.memo,
    booking_entry: form.value.bookingEntry
  }

  try {
    let result
    if (editMode.value) {
      // Update: pass DB id separately
      result = await store.dispatch('eshop/updateOrderTransaction', {
        id: props.existingTransaction.id,
        transaction: payload
      })
    } else {
      // Create: backend generates transaction_id
      result = await store.dispatch('eshop/createOrderTransaction', {
        transaction: payload
      })
    }

    if (result.success !== false) {
      emit('saved', result)
      close()
    } else {
      alert(result.error || 'Failed to save transaction')
    }
  } catch (error) {
    alert(error.message || 'An error occurred while saving')
  } finally {
    submitting.value = false
  }
}
</script>
